const STORAGE_KEY = 'minimind_cookie_consent'

type ConsentStatus = 'accepted' | 'declined'

/**
 * Composable to manage GDPR cookie consent state.
 * Uses useState to share a single instance across all components.
 * Stores the user's preference in localStorage under `minimind_cookie_consent`.
 */
export function useCookieConsent() {
  const consentStatus = useState<ConsentStatus | null>('cookieConsent', () => {
    if (import.meta.server) return null
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'accepted' || stored === 'declined') return stored
    return null
  })

  /** Whether the consent banner should be displayed. */
  const showBanner = computed(() => consentStatus.value === null)

  /** Whether the user has accepted cookies. */
  const hasAccepted = computed(() => consentStatus.value === 'accepted')

  function saveConsent(status: ConsentStatus): void {
    consentStatus.value = status
    localStorage.setItem(STORAGE_KEY, status)
  }

  function acceptCookies(): void {
    saveConsent('accepted')
  }

  function declineCookies(): void {
    saveConsent('declined')
  }

  return {
    consentStatus: readonly(consentStatus),
    showBanner,
    hasAccepted,
    acceptCookies,
    declineCookies,
  }
}
